import { Sponsor } from '../models'

/**
 * Checks if a sponsor is currently valid based on start and end dates
 */
export const isSponsorValid = (sponsor: Sponsor): boolean => {
  const now = new Date()

  // Check if sponsor is active
  if (sponsor.active === false) {
    return false
  }

  // Check start date
  if (sponsor.start) {
    const startDate = new Date(sponsor.start)
    if (now < startDate) {
      return false
    }
  }

  // Check end date
  if (sponsor.end) {
    const endDate = new Date(sponsor.end)
    if (now > endDate) {
      return false
    }
  }

  return true
}

/**
 * Gets the most valid sponsor from a list of sponsors
 * Priority is given to sponsors with higher priority values
 * For sponsors with equal priority, the most recent one is chosen
 */
export const getMostValidSponsor = (sponsors: Sponsor[]): Sponsor | null => {
  if (!sponsors || sponsors.length === 0) {
    return null
  }

  // Filter valid sponsors
  const validSponsors = sponsors.filter(isSponsorValid)

  if (validSponsors.length === 0) {
    return null
  }

  // Sort by priority (ascending) and then by updatedAt (descending)
  const sortedSponsors = validSponsors.sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority - b.priority // Lower priority value first
    }
    // If priorities are equal, sort by most recent update
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  })

  return sortedSponsors[0]
}

/**
 * Gets up to 3 sponsors sorted by priority and validity
 * Primary sponsor (highest priority) comes first, followed by secondary sponsors
 */
export const getTopSponsors = (sponsors: Sponsor[], maxCount: number = 3): Sponsor[] => {
  if (!sponsors || sponsors.length === 0) {
    return []
  }

  // Filter valid sponsors
  const validSponsors = sponsors.filter(isSponsorValid)

  if (validSponsors.length === 0) {
    return []
  }

  // Sort by priority (ascending) and then by updatedAt (descending)
  const sortedSponsors = validSponsors.sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority - b.priority // Lower priority value first
    }
    // If priorities are equal, sort by most recent update
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  })

  // Return up to maxCount sponsors
  return sortedSponsors.slice(0, maxCount)
}

/**
 * Builds a link for a sponsor's website. Admins may enter the address with or without the scheme
 * ("https://example.com" or "www.example.com"), so only add https:// when it's missing.
 */
export const getSponsorHref = (url?: string | null): string | undefined => {
  const trimmed = url?.trim()
  if (!trimmed) return undefined
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  // tolerate protocol-relative values ("//example.com") saved before this helper existed
  return `https://${trimmed.replace(/^\/+/, '')}`
}
